"use client"

import * as React from "react"
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
} from "@tanstack/react-table"
// IntersectionObserver 기반 간단 useInView 훅 (react-intersection-observer 대체)
function useInView({ threshold = 0, rootMargin = "0px", skip = false } = {}) {
  const [node, setNode] = React.useState<HTMLElement | null>(null)
  const [inView, setInView] = React.useState(false)

  const setRef = React.useCallback(
    (el: HTMLElement | null) => {
      setNode(el)
    },
    []
  )

  React.useEffect(() => {
    if (skip || !node) return
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold, rootMargin }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [node, skip, threshold, rootMargin])

  return { ref: setRef, inView }
}

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"

function SortIcon({ column }: { column: any }) {
  const sorted = column.getIsSorted()
  return (
    <svg
      width="9"
      height="14"
      viewBox="0 0 9 14"
      className="shrink-0 fill-current ml-auto"
    >
      {/* ▲ asc triangle */}
      <path
        d="M4.5 1 L8 5.5 L1 5.5 Z"
        fillOpacity={sorted === "asc" ? 1 : 0.3}
      />
      {/* ▼ desc triangle */}
      <path
        d="M4.5 13 L1 8.5 L8 8.5 Z"
        fillOpacity={sorted === "desc" ? 1 : 0.3}
      />
    </svg>
  )
}

const LazyVirtualTable = React.lazy(() =>
  import("./data-table-virtual").then((mod) => ({ default: mod.VirtualTable }))
)

interface SummaryLabel {
  text: string
  colSpan: number
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  className?: string
  getRowId?: (originalRow: TData, index: number, parent?: any) => string
  onRowClick?: (row: TData, event: React.MouseEvent) => void
  onRowContextMenu?: (row: TData, event: React.MouseEvent) => void
  pagination?: any
  bordered?: boolean
  sticky?: boolean
  stickyHeader?: boolean
  scroll?: any
  size?: any
  rowClassName?: string | ((row: TData) => string)
  rowStyle?: any
  variant?: any
  // Infinite Scroll Props
  infiniteScroll?: boolean
  onLoadMore?: () => void | Promise<void>
  hasMore?: boolean
  isLoadingMore?: boolean
  loadingComponent?: React.ReactNode
  // Virtual Scroll Props
  virtualScroll?: boolean
  rowHeight?: number
  // Sorting Props
  initialSorting?: SortingState
  onSortingChange?: (sorting: SortingState) => void
  manualSorting?: boolean
  // Gradient Header
  gradientHeader?: boolean
  // Active Row Highlight
  activeRowId?: string | null
  // Expandable Row Props
  expandable?: {
    render: (row: TData) => React.ReactNode
    onExpand?: (row: TData, expanded: boolean) => void
  }
  // Summary Row Props
  summaryData?: Partial<TData>
  summaryPosition?: 'top' | 'bottom' | 'both'
  summaryLabel?: SummaryLabel
  summaryClassName?: string
}

export function DataTable<TData, TValue>({
  columns,
  data,
  className,
  getRowId,
  onRowClick,
  onRowContextMenu,
  stickyHeader = false,
  // Infinite Scroll Props
  infiniteScroll = false,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  loadingComponent,
  // Virtual Scroll Props
  virtualScroll = false,
  rowHeight = 35,
  // Sorting Props
  initialSorting = [],
  onSortingChange: onSortingChangeProp,
  manualSorting = false,
  // Gradient Header
  gradientHeader = false,
  // Row ClassName
  rowClassName,
  // Active Row Highlight
  activeRowId,
  // Expandable Row Props
  expandable,
  // Summary Row Props
  summaryData,
  summaryPosition = 'bottom',
  summaryLabel = { text: '합계', colSpan: 1 },
  summaryClassName,
}: DataTableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = React.useState({})
  const [sorting, setSorting] = React.useState<SortingState>(initialSorting)
  const [expandedRows, setExpandedRows] = React.useState<Set<string>>(new Set())

  // 외부 콜백에 정렬 변경 알림
  const handleSortingChange: typeof setSorting = React.useCallback((updaterOrValue) => {
    setSorting((prev) => {
      const next = typeof updaterOrValue === "function" ? updaterOrValue(prev) : updaterOrValue
      onSortingChangeProp?.(next)
      return next
    })
  }, [onSortingChangeProp])

  const table = useReactTable({
    data,
    columns,
    enableSorting: true,
    getCoreRowModel: getCoreRowModel(),
    ...(infiniteScroll
      ? {}
      : { getPaginationRowModel: getPaginationRowModel() }),
    manualSorting,
    ...(manualSorting ? {} : { getSortedRowModel: getSortedRowModel() }),
    onRowSelectionChange: setRowSelection,
    onSortingChange: handleSortingChange,
    getRowId,
    state: {
      rowSelection,
      sorting,
    },
  })

  // Infinite Scroll: Sentinel 요소 감지
  const { ref: sentinelRef, inView } = useInView({
    threshold: 0,
    rootMargin: "100px", // 하단 100px 전에 미리 로드 시작
    skip: !infiniteScroll, // infiniteScroll이 false면 감지 비활성화
  })

  // inView 변경 시 자동 로드
  React.useEffect(() => {
    if (infiniteScroll && inView && hasMore && !isLoadingMore && onLoadMore) {
      onLoadMore()
    }
  }, [infiniteScroll, inView, hasMore, isLoadingMore, onLoadMore])

  // 기본 로딩 패널
  const defaultLoadingComponent = (
    <div className="flex justify-center items-center py-4 bg-muted/30 border-t">
      <div className="flex items-center gap-2 px-4 py-2 bg-background rounded-md shadow-sm border">
        <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />
        <span className="text-sm text-muted-foreground">데이터 로딩 중...</span>
      </div>
    </div>
  )

  const hasData = table.getRowModel().rows?.length > 0
  const rows = table.getRowModel().rows

  // Ratchet layout: 컬럼에 명시적 size가 있으면 열 너비 래칫 적용
  // 래칫 = 컬럼이 커지면 커진 크기 유지, 줄어들지 않음
  const allLeafColumns = table.getAllLeafColumns()
  const hasExplicitSizes = allLeafColumns.some(col => col.columnDef.size !== undefined)
  const colMaxWidthsRef = React.useRef<Record<string, number>>({})
  const containerRef = React.useRef<HTMLDivElement>(null)

  // Ratchet: 렌더 후 헤더 셀 너비를 측정하여 최대값 이하로 줄어들지 않게 설정
  React.useLayoutEffect(() => {
    if (!hasExplicitSizes || !containerRef.current) return
    const cells = containerRef.current.querySelectorAll<HTMLElement>('th[data-col-id]')
    cells.forEach((cell) => {
      const colId = cell.getAttribute('data-col-id')!
      const col = allLeafColumns.find(c => c.id === colId)
      if (!col || col.columnDef.size === undefined) return
      const rendered = cell.offsetWidth
      const prevMax = colMaxWidthsRef.current[colId] || col.columnDef.size
      const newMax = Math.max(prevMax, rendered)
      colMaxWidthsRef.current[colId] = newMax
      cell.style.minWidth = `${newMax}px`
    })
  })

  // Summary Row 렌더링
  const renderSummaryRow = React.useCallback(() => {
    if (!summaryData) return null

    const leafColumns = table.getAllLeafColumns()
    const mergeCount = summaryLabel.colSpan

    return (
      <TableRow className={cn("data-table-summary-row hover:bg-transparent", summaryClassName)}>
        {/* 병합된 라벨 셀 */}
        <TableCell
          colSpan={mergeCount}
          className="data-table-summary-cell data-table-summary-label"
        >
          {summaryLabel.text}
        </TableCell>

        {/* 나머지 데이터 셀 - 컬럼의 cell 렌더러 활용 */}
        {leafColumns.slice(mergeCount).map((column) => {
          const value = (summaryData as any)[column.id]
          let rendered: React.ReactNode = value ?? ''

          if (value != null) {
            const cellDef = column.columnDef.cell
            if (typeof cellDef === 'function') {
              try {
                rendered = cellDef({
                  getValue: () => value,
                  row: { original: summaryData },
                  column,
                  cell: { id: `summary_${column.id}`, getValue: () => value },
                  table,
                  renderValue: () => value,
                } as any)
              } catch {
                rendered = value
              }
            }
          }

          return (
            <TableCell
              key={column.id}
              className={cn("data-table-summary-cell", (column.columnDef.meta as any)?.cellClassName)}
            >
              {rendered}
            </TableCell>
          )
        })}
      </TableRow>
    )
  }, [summaryData, summaryLabel, summaryClassName, table])

  // Virtual Scroll 모드 - lazy loaded
  if (virtualScroll && hasData) {
    return (
      <React.Suspense fallback={<div className="flex items-center justify-center h-full text-muted-foreground">로딩 중...</div>}>
        <LazyVirtualTable
          headerGroups={table.getHeaderGroups()}
          rows={rows}
          dataLength={data.length}
          rowHeight={rowHeight}
          className={className}
          onRowClick={onRowClick}
          onRowContextMenu={onRowContextMenu}
        />
      </React.Suspense>
    )
  }

  // 기존 렌더링 (일반 모드 / Infinite Scroll 모드)
  return (
    <div ref={containerRef} className={cn("overflow-hidden flex flex-col bg-white h-full", className)}>
      {/* 스크롤 영역 (수직+수평 통합) - table-container의 overflow를 무효화하여 sticky 헤더 지원 */}
      <div className="flex-1 min-h-0 overflow-auto [&_[data-slot=table-container]]:!overflow-visible">
        <Table className={cn(!hasData && "flex-shrink-0")}>
          <TableHeader gradient={gradientHeader} className={cn(stickyHeader && "sticky top-0 z-20", stickyHeader && !gradientHeader && "bg-background")}>
            {table.getHeaderGroups().map((headerGroup, groupIdx) => {
              const headerGroups = table.getHeaderGroups()
              const headerGroupCount = headerGroups.length

              return (
                <TableRow
                  key={headerGroup.id}
                  className={cn(
                    "hover:bg-transparent",
                    stickyHeader && !gradientHeader && "bg-background"
                  )}
                  style={stickyHeader ? {
                    position: 'sticky',
                    top: `${groupIdx * 33}px`,
                    zIndex: 20 - groupIdx
                  } : undefined}
                >
                  {headerGroup.headers
                    .filter(header => {
                      // 단일 depth 테이블은 기존 로직 유지
                      if (headerGroupCount <= 1) return !header.isPlaceholder

                      // top-level leaf column: 그룹에 속하지 않는 단독 컬럼
                      const isTopLevelLeaf = !header.column.columnDef.columns && !header.column.parent

                      if (header.isPlaceholder) {
                        // top-level leaf의 placeholder는 유지 (Row 0에서 rowSpan으로 렌더링)
                        return isTopLevelLeaf
                      }

                      // top-level leaf가 deeper row에서 중복 렌더링되지 않도록 제거
                      if (isTopLevelLeaf && headerGroup.depth > 0) return false

                      return true
                    })
                    .map((header) => {
                      const isLeafColumn = !header.column.columnDef.columns
                      const isTopLevelLeaf = isLeafColumn && !header.column.parent

                      // top-level leaf: 전체 header row를 병합
                      const rowSpan = isTopLevelLeaf && headerGroupCount > 1
                        ? headerGroupCount
                        : undefined
                      const isGroupHeader = header.colSpan > 1

                      return (
                        <TableHead
                          key={header.id}
                          data-col-id={isLeafColumn ? header.column.id : undefined}
                          colSpan={header.colSpan > 1 ? header.colSpan : undefined}
                          rowSpan={rowSpan && rowSpan > 1 ? rowSpan : undefined}
                          className={cn(
                            isGroupHeader && "text-center bg-muted/50 font-semibold border-b",
                            rowSpan && rowSpan > 1 && "align-middle",
                            (header.column.columnDef.meta as any)?.headerClassName
                          )}
                          style={isLeafColumn && header.column.columnDef.size !== undefined ? {
                            minWidth: colMaxWidthsRef.current[header.column.id] || header.column.columnDef.size,
                          } : undefined}
                        >
                          {header.column.getCanSort() ? (
                            <button
                              className="inline-flex items-center gap-1 w-full select-none [font-size:inherit] [font-weight:inherit] [color:inherit]"
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              <span className="flex-1 text-center">{flexRender(header.column.columnDef.header, header.getContext())}</span>
                              <SortIcon column={header.column} />
                            </button>
                          ) : (
                            flexRender(header.column.columnDef.header, header.getContext())
                          )}
                        </TableHead>
                      )
                    })}
                </TableRow>
              )
            })}
          </TableHeader>

          {/* Summary Row - Top */}
          {summaryData && (summaryPosition === 'top' || summaryPosition === 'both') && (
            <tbody data-slot="table-summary-top" className="bg-card">
              {renderSummaryRow()}
            </tbody>
          )}

          <TableBody>
            {hasData ? (
              <>
                {rows.map((row) => {
                  const isExpanded = expandable && expandedRows.has(row.id)
                  return (
                    <React.Fragment key={row.id}>
                      <TableRow
                        data-state={row.getIsSelected() && "selected"}
                        className={cn(
                          (onRowClick || expandable) && "cursor-pointer",
                          activeRowId != null && row.id === activeRowId && "bg-primary/10",
                          isExpanded && "bg-muted/30",
                          typeof rowClassName === "function" ? rowClassName(row.original) : rowClassName
                        )}
                        onClick={(e) => {
                          if (expandable) {
                            const next = !expandedRows.has(row.id)
                            setExpandedRows(prev => {
                              const s = new Set(prev)
                              next ? s.add(row.id) : s.delete(row.id)
                              return s
                            })
                            expandable.onExpand?.(row.original, next)
                          }
                          onRowClick?.(row.original, e)
                        }}
                        onContextMenu={(e) => onRowContextMenu && onRowContextMenu(row.original, e)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            className={(cell.column.columnDef.meta as any)?.cellClassName}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                      {isExpanded && (
                        <tr>
                          <td colSpan={row.getVisibleCells().length} className="p-0 bg-muted/10">
                            {expandable.render(row.original)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}

                {/* Infinite Scroll Sentinel - 데이터가 있을 때만 렌더링 */}
                {infiniteScroll && (
                  <tr ref={sentinelRef} aria-hidden="true">
                    <td colSpan={columns.length} className="h-1 p-0" />
                  </tr>
                )}

              </>
            ) : null}
          </TableBody>

          {/* Summary Row - Bottom */}
          {summaryData && (summaryPosition === 'bottom' || summaryPosition === 'both') && (
            <tfoot data-slot="table-summary-bottom" className="bg-card">
              {renderSummaryRow()}
            </tfoot>
          )}
        </Table>

        {/* Empty State - 테이블 외부에서 flex로 높이 채우기 */}
        {!hasData && (
          <div className="flex-1 flex items-center justify-center bg-white text-gray-500">
            검색 결과가 없습니다
          </div>
        )}

        {/* 로딩 스피너 - 테이블 외부에 위치 */}
        {infiniteScroll && isLoadingMore && (
          loadingComponent || defaultLoadingComponent
        )}

        {/* 끝 메시지 - 데이터가 있고 더 이상 없을 때 */}
        {infiniteScroll && !hasMore && data.length > 0 && (
          <div className="text-center py-3 text-sm text-muted-foreground border-t">
            모든 데이터를 불러왔습니다
          </div>
        )}
      </div>
    </div>
  )
}
